import React, { useState, useEffect, useRef } from 'react';
import './ProgramOutcomesGenerator.css';
import { API_BASE_URL } from '../config';
import ProgramOutcomesTable from './ProgramOutcomesTable';
import { ProgressBar, useSimulatedProgress } from './CurriculumPlanGenerator';
import {
  approveProgramOutcomes,
  readApprovedProgramOutcomes,
  updateApprovedProgramOutcomes,
} from '../utils/approvedProgramOutcomes';
import { downloadProgramOutcomesPdf } from '../utils/programOutcomesPdf';

function ProgramOutcomesGenerator({
  programId, programName, userType, programAdminToken, adminToken, onContinueToPlan,
  approvalRequired = false,
}) {
  const [mode, setMode] = useState('idle'); // 'idle' | 'loading' | 'result' | 'error'
  const [error, setError] = useState('');
  const [existingOutcomes, setExistingOutcomes] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [existingOutcomesError, setExistingOutcomesError] = useState('');
  const [outcomesData, setOutcomesData] = useState(null);
  const [extraInstructions, setExtraInstructions] = useState('');
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [approvedSnapshot, setApprovedSnapshot] = useState(() => readApprovedProgramOutcomes(programId));
  const [editingApproved, setEditingApproved] = useState(false);
  const [approvedDraft, setApprovedDraft] = useState([]);
  const [approvedEditError, setApprovedEditError] = useState('');
  const [approvedPdfError, setApprovedPdfError] = useState('');
  const [downloadingApprovedPdf, setDownloadingApprovedPdf] = useState(false);
  const [curriculumInstructions, setCurriculumInstructions] = useState('');
  const [regeneratingApproved, setRegeneratingApproved] = useState(false);
  const automaticConsolidationStarted = useRef(false);
  const progress = useSimulatedProgress();

  useEffect(() => {
    const storedSnapshot = readApprovedProgramOutcomes(programId);
    setApprovedSnapshot(storedSnapshot);
    setApprovedDraft(storedSnapshot?.outcomes?.map((outcome) => ({ ...outcome })) || []);
    setEditingApproved(false);
    setApprovedEditError('');
    setRegeneratingApproved(false);
    setCurriculumInstructions('');
  }, [programId]);

  const buildAuthHeaders = (includeContentType = true) => {
    const headers = includeContentType ? { 'Content-Type': 'application/json' } : {};
    const token = programAdminToken || adminToken;
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  const handleDownloadApprovedPdf = async () => {
    if (!approvedSnapshot?.outcomes?.length || downloadingApprovedPdf) return;
    setDownloadingApprovedPdf(true);
    setApprovedPdfError('');
    try {
      await downloadProgramOutcomesPdf(approvedSnapshot.outcomes, { programName });
    } catch (error) {
      console.error('Onaylanmış program çıktıları PDF hatası:', error);
      setApprovedPdfError('PDF oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setDownloadingApprovedPdf(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchExisting = async () => {
      if (!programId) return;
      setLoadingExisting(true);
      setExistingOutcomesError('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/program-outcomes`, {
          headers: buildAuthHeaders(),
        });
        const data = await res.json().catch(() => []);
        if (!res.ok) {
          throw new Error(data?.error || `Program çıktıları alınamadı (HTTP ${res.status})`);
        }
        if (!cancelled) setExistingOutcomes(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          setExistingOutcomes([]);
          setExistingOutcomesError(err?.message || 'Program çıktıları sunucudan alınamadı.');
        }
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    };
    fetchExisting();
    return () => { cancelled = true; };
    // Yetki bilgileri oturum açılışında asenkron yüklendiği için token hazır
    // olduğunda isteği yeniden çalıştırmak gerekir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, userType, programAdminToken, adminToken]);

  const handleFilesSelected = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    setFileError('');

    const allowedExtensions = ['docx', 'pdf', 'pptx'];
    const invalidFile = selectedFiles.find((file) => {
      const extension = file.name.split('.').pop().toLowerCase();
      return !allowedExtensions.includes(extension);
    });
    if (invalidFile) {
      setFileError('Yalnızca Word (.docx), PDF (.pdf) ve PowerPoint (.pptx) dosyaları eklenebilir.');
      return;
    }
    if (selectedFiles.some((file) => file.size > 8 * 1024 * 1024)) {
      setFileError('Her belge en fazla 8 MB olabilir.');
      return;
    }

    setReferenceFiles((currentFiles) => {
      const combined = [...currentFiles];
      selectedFiles.forEach((file) => {
        const isDuplicate = combined.some(
          (item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified
        );
        if (!isDuplicate) combined.push(file);
      });
      if (combined.length > 5) {
        setFileError('En fazla 5 belge ekleyebilirsiniz.');
        return currentFiles;
      }
      if (combined.reduce((total, file) => total + file.size, 0) > 15 * 1024 * 1024) {
        setFileError('Belgelerin toplam boyutu en fazla 15 MB olabilir.');
        return currentFiles;
      }
      return combined;
    });
  };

  const removeReferenceFile = (indexToRemove) => {
    setReferenceFiles((files) => files.filter((_, index) => index !== indexToRemove));
    setFileError('');
  };

  const handleGenerate = async () => {
    setMode('loading');
    setError('');
    // Gerçek istekler genelde 60-90sn sürüyor (backend loglarına göre), tahmin buna göre kalibre edildi.
    progress.start(80000);
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const formData = new FormData();
        const limitInstruction = attempt === 1
          ? '\nZORUNLU DÜZELTME: Tüm mevcut çıktıları birlikte değerlendir; kapsamı koruyan, birbirini tekrar etmeyen en az 10 ve en fazla 13 nihai program çıktısı üret.'
          : '';
        formData.append('extra_instructions', `${extraInstructions}${limitInstruction}`.trim());
        if (regeneratingApproved && approvedSnapshot?.outcomes?.length) {
          formData.append('current_outcomes', JSON.stringify(approvedSnapshot.outcomes));
        }
        referenceFiles.forEach((file) => formData.append('reference_files', file));
        const res = await fetch(`${API_BASE_URL}/api/program/${programId}/program-ciktilari/oneri`, {
          method: 'POST',
          headers: buildAuthHeaders(false),
          body: formData,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError((json && json.error) || 'Program çıktıları önerisi oluşturulamadı, tekrar deneyin');
          setMode('error');
          progress.reset();
          return;
        }
        if (Array.isArray(json.yeni_ciktilar)
          && json.yeni_ciktilar.length >= 10
          && json.yeni_ciktilar.length <= 13) {
          progress.complete();
          setOutcomesData(json);
          setMode('result');
          return;
        }
      }
      setError('Önerilen program çıktıları 10-13 aralığına getirilemedi. Lütfen yeniden deneyin.');
      setMode('error');
      progress.reset();
    } catch (err) {
      setError('Program çıktıları önerisi oluşturulamadı, tekrar deneyin');
      setMode('error');
      progress.reset();
    }
  };

  const generatedOutcomeCount = Array.isArray(outcomesData?.yeni_ciktilar)
    ? outcomesData.yeni_ciktilar.length
    : 0;

  useEffect(() => {
    const countIsInvalid = generatedOutcomeCount < 10 || generatedOutcomeCount > 13;
    if (mode === 'result' && countIsInvalid && !automaticConsolidationStarted.current) {
      automaticConsolidationStarted.current = true;
      handleGenerate();
    } else if (mode === 'result' && !countIsInvalid) {
      automaticConsolidationStarted.current = false;
    }
    // handleGenerate her renderda yeniden oluştuğundan yalnızca sonuç durumu ve sayı izlenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, generatedOutcomeCount]);

  const handleApproveAndContinue = (outcomes, planInstructions = curriculumInstructions) => {
    const result = approvedSnapshot
      ? updateApprovedProgramOutcomes(programId, programName, outcomes)
      : approveProgramOutcomes(programId, programName, outcomes);
    if (!result.ok) return { error: result.error };
    setApprovedSnapshot(result.snapshot);
    setRegeneratingApproved(false);
    if (onContinueToPlan) onContinueToPlan(result.snapshot.outcomes, String(planInstructions || '').trim());
    return { ok: true };
  };

  const startApprovedEditing = () => {
    setApprovedDraft(approvedSnapshot.outcomes.map((outcome) => ({ ...outcome })));
    setApprovedEditError('');
    setEditingApproved(true);
  };

  const updateApprovedDraft = (index, name) => {
    setApprovedDraft((rows) => rows.map((row, rowIndex) => (
      rowIndex === index ? { ...row, name } : row
    )));
  };

  const moveApprovedDraft = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= approvedDraft.length) return;
    setApprovedDraft((rows) => {
      const reordered = [...rows];
      [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
      return reordered.map((row, rowIndex) => ({ ...row, number: rowIndex + 1 }));
    });
  };

  const removeApprovedDraft = (index) => {
    setApprovedDraft((rows) => rows
      .filter((_, rowIndex) => rowIndex !== index)
      .map((row, rowIndex) => ({ ...row, number: rowIndex + 1 })));
  };

  const addApprovedDraft = () => {
    if (approvedDraft.length >= 13) return;
    setApprovedDraft((rows) => [...rows, { number: rows.length + 1, name: '' }]);
  };

  const saveApprovedEditing = () => {
    const result = updateApprovedProgramOutcomes(programId, programName, approvedDraft);
    if (!result.ok) {
      setApprovedEditError(result.error);
      return;
    }
    setApprovedSnapshot(result.snapshot);
    setApprovedDraft(result.snapshot.outcomes.map((outcome) => ({ ...outcome })));
    setApprovedEditError('');
    setEditingApproved(false);
  };

  if (mode === 'loading') {
    return (
      <div className="plan-generator__loading">
        <ProgressBar label="Program çıktıları güncel dünya/Türkiye trendlerine göre araştırılıyor..." percent={progress.percent} />
      </div>
    );
  }

  if (mode === 'error') {
    return (
      <div className="plan-generator__error">
        <div className="plan-generator__error-message">{error}</div>
        <button onClick={handleGenerate} className="button plan-generator__retry">
          Tekrar Dene
        </button>
        <button onClick={() => { setMode('idle'); setError(''); }} className="button po-gen__edit-inputs">
          Talimat ve Belgeleri Düzenle
        </button>
      </div>
    );
  }

  if (mode === 'result' && outcomesData
    && ((outcomesData.yeni_ciktilar || []).length < 10
      || (outcomesData.yeni_ciktilar || []).length > 13)) {
    return (
      <div className="plan-generator__loading">
        <ProgressBar
          label="Program çıktıları 10-13 öneri aralığına getiriliyor..."
          percent={progress.percent}
        />
      </div>
    );
  }

  if (mode === 'result' && outcomesData) {
    return (
      <ProgramOutcomesTable
        programName={programName}
        planInstructions={curriculumInstructions}
        onPlanInstructionsChange={setCurriculumInstructions}
        eskiCiktilar={outcomesData.eski_ciktilar}
        yeniCiktilar={outcomesData.yeni_ciktilar}
        kaldirilanlar={outcomesData.kaldirilanlar}
        onRegenerate={handleGenerate}
        onContinueToPlan={handleApproveAndContinue}
        onCancel={() => {
          setMode('idle');
          setOutcomesData(null);
          if (regeneratingApproved) setRegeneratingApproved(false);
        }}
      />
    );
  }

  if (approvedSnapshot && !regeneratingApproved) {
    return (
      <section className="po-approved" aria-labelledby="po-approved-title">
        <div className="po-approved__header">
          <div>
            <span className="po-approved__badge">Ders planı için seçilen PÇ'ler</span>
            <h3 id="po-approved-title">Ders Planında Kullanılacak Program Çıktıları</h3>
            <p>
              Program çıktılarını düzenleyebilir ve güncel listeye göre yeni ders planı önerisi oluşturabilirsiniz.
            </p>
          </div>
          <div className="po-approved__header-actions">
            <strong>{editingApproved ? approvedDraft.length : approvedSnapshot.outcomes.length} PÇ</strong>
            {!editingApproved && (
              <button
                type="button"
                className="button po-approved__pdf-button"
                onClick={handleDownloadApprovedPdf}
                disabled={downloadingApprovedPdf || approvedSnapshot.outcomes.length === 0}
              >
                {downloadingApprovedPdf ? 'PDF Hazırlanıyor…' : 'PDF Olarak İndir'}
              </button>
            )}
          </div>
        </div>

        {editingApproved ? (
          <div className="po-approved__editor">
            {approvedDraft.map((outcome, index) => (
              <div className="po-approved__edit-row" key={`approved-edit-${index}`}>
                <span>PÇ {index + 1}</span>
                <textarea
                  value={outcome.name}
                  onChange={(event) => updateApprovedDraft(index, event.target.value)}
                  rows={Math.max(2, Math.ceil(String(outcome.name || '').length / 90))}
                  aria-label={`PÇ ${index + 1} metni`}
                  placeholder="Program çıktısını yazın"
                />
                <div className="po-approved__edit-controls">
                  <button type="button" onClick={() => moveApprovedDraft(index, -1)} disabled={index === 0} title="Yukarı taşı">↑</button>
                  <button type="button" onClick={() => moveApprovedDraft(index, 1)} disabled={index === approvedDraft.length - 1} title="Aşağı taşı">↓</button>
                  <button type="button" onClick={() => removeApprovedDraft(index)} className="po-approved__delete" title="Çıktıyı kaldır">×</button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="po-approved__add-button"
              onClick={addApprovedDraft}
              disabled={approvedDraft.length >= 13}
            >
              + Yeni Program Çıktısı Ekle
            </button>
            {approvedEditError && <div className="po-approved__error" role="alert">{approvedEditError}</div>}
          </div>
        ) : (
          <ol className="po-approved__list">
            {approvedSnapshot.outcomes.map((outcome) => (
              <li key={outcome.number}>
                <span>PÇ {outcome.number}</span>
                <p>{outcome.name}</p>
              </li>
            ))}
          </ol>
        )}

        {!editingApproved && (
          <section className="po-approved__plan-instructions" aria-labelledby="po-approved-plan-instructions-title">
            <div>
              <h4 id="po-approved-plan-instructions-title">Ders Planı İçin Ek Talimat <span>Opsiyonel</span></h4>
              <p>Yeni ders planında uygulanmasını istediğiniz özel ölçütleri belirtin.</p>
            </div>
            <textarea
              value={curriculumInstructions}
              onChange={(event) => setCurriculumInstructions(event.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Örnek: Uygulamalı dersleri artır, mevcut X dersini koru veya seçmeli dersleri 5. dönemden itibaren planla."
              aria-label="Ders planı için ek talimat"
            />
            <small>{curriculumInstructions.length}/2000</small>
          </section>
        )}

        <div className="po-approved__footer">
          {editingApproved ? (
            <>
              <button
                type="button"
                className="button po-approved__cancel-button"
                onClick={() => {
                  setApprovedDraft(approvedSnapshot.outcomes.map((outcome) => ({ ...outcome })));
                  setApprovedEditError('');
                  setEditingApproved(false);
                }}
              >
                Vazgeç
              </button>
              <button type="button" className="button po-approved__save-button" onClick={saveApprovedEditing}>
                Değişiklikleri Kaydet
              </button>
            </>
          ) : (
            <>
              <button type="button" className="button po-approved__edit-button" onClick={startApprovedEditing}>
                Program Çıktılarını Düzenle
              </button>
              <button
                type="button"
                className="button po-approved__regenerate-button"
                onClick={() => {
                  setExtraInstructions('');
                  setReferenceFiles([]);
                  setFileError('');
                  setError('');
                  setMode('idle');
                  setRegeneratingApproved(true);
                }}
              >
                PÇ'leri Yeniden Oluştur
              </button>
              <button
                type="button"
                className="button po-approved__plan-button"
                onClick={() => onContinueToPlan && onContinueToPlan(
                  approvedSnapshot.outcomes,
                  curriculumInstructions.trim(),
                )}
              >
                Bu PÇ'lere Göre Ders Planı Öner
              </button>
            </>
          )}
        </div>
        {approvedPdfError && <div className="po-approved__pdf-error" role="alert">{approvedPdfError}</div>}
      </section>
    );
  }

  return (
    <div className="po-gen">
      {regeneratingApproved && (
        <div className="po-gen__regenerate-note">
          Mevcut PÇ listeniz başlangıç alınarak yeni program çıktıları önerilecektir. Ek talimat veya referans
          belge ekleyebilir, oluşan öneriden yeni nihai listenizi seçebilirsiniz.
        </div>
      )}
      {approvalRequired && (
        <div className="po-gen__approval-required" role="alert">
          Ders planı oluşturabilmek için önce kullanılacak nihai program çıktıları listesini belirleyin.
          Ders planı yalnızca seçtiğiniz bu listeye göre hazırlanacaktır.
        </div>
      )}
      <div className="po-gen__intro">
        <p>
          Bu ekran, programın mevcut çıktılarını ilgili fakülte ve bölümün Türkiye'deki ve dünyadaki güncel
          eğitim esasları, mesleki yeterlilikleri, alan eğilimleri ve uygun emsal üniversite programları ışığında
          yapay zekâ ile inceleyip güncelleme önerisi sunar. Yalnızca bu programa gerçekten uygun çerçeveler kullanılır.
          Ders planı oluşturmadan önce program çıktılarının güncel olduğundan emin olun.
        </p>
      </div>

      <div className="po-gen__existing">
        <h3>{regeneratingApproved ? 'Şu Anda Kullanılan Program Çıktıları' : 'Mevcut Program Çıktıları'}</h3>
        {!regeneratingApproved && loadingExisting ? (
          <div className="po-gen__loading-text">Yükleniyor…</div>
        ) : !regeneratingApproved && existingOutcomesError ? (
          <div className="po-gen__error" role="alert">{existingOutcomesError}</div>
        ) : (regeneratingApproved ? approvedSnapshot.outcomes : existingOutcomes).length === 0 ? (
          <div className="po-gen__empty">Bu program için henüz program çıktısı tanımlanmamış.</div>
        ) : (
          <ul className="po-gen__list">
            {(regeneratingApproved ? approvedSnapshot.outcomes : existingOutcomes).map((o) => (
              <li key={o.id || o.number}><strong>{o.number}.</strong> {o.name}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="po-gen__extra">
        <label htmlFor="po-extra-instructions">Ek talimat (opsiyonel)</label>
        <textarea
          id="po-extra-instructions"
          value={extraInstructions}
          onChange={(e) => setExtraInstructions(e.target.value)}
          placeholder="Yapay zekaya öneri üretirken dikkate alması için ek talimat yazın (ör. belirli bir yetkinliğe ağırlık verilmesi, belirli bir çıktının korunması vb.)"
          rows={3}
          maxLength={2000}
        />

        <div className="po-gen__references">
          <div className="po-gen__references-heading">
            <div>
              <strong>Referans belgeler (opsiyonel)</strong>
              <span>Yapay zekâ bu belgeleri okuyarak yeni program çıktılarını oluşturur.</span>
            </div>
            <label className="po-gen__file-button" htmlFor="po-reference-files">
              Belge Ekle
            </label>
            <input
              id="po-reference-files"
              className="po-gen__file-input"
              type="file"
              accept=".docx,.pdf,.pptx"
              multiple
              onChange={handleFilesSelected}
            />
          </div>
          <div className="po-gen__file-help">Word, PDF veya PowerPoint · En fazla 5 belge · Belge başına 8 MB, toplam 15 MB</div>
          {fileError && <div className="po-gen__file-error">{fileError}</div>}
          {referenceFiles.length > 0 && (
            <ul className="po-gen__file-list">
              {referenceFiles.map((file, index) => (
                <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                  <span title={file.name}>{file.name}</span>
                  <small>{(file.size / (1024 * 1024)).toFixed(1)} MB</small>
                  <button type="button" onClick={() => removeReferenceFile(index)} aria-label={`${file.name} belgesini kaldır`}>
                    Kaldır
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="po-gen__actions">
        {regeneratingApproved && (
          <button
            type="button"
            className="button po-gen__back-to-approved"
            onClick={() => setRegeneratingApproved(false)}
          >
            Mevcut PÇ Listesine Dön
          </button>
        )}
        <button className="button po-gen__generate-btn" onClick={handleGenerate} disabled={!programId}>
          {regeneratingApproved ? 'Yeni PÇ Önerisi Oluştur' : 'Program Çıktılarını Güncelle'}
        </button>
      </div>
    </div>
  );
}

export default ProgramOutcomesGenerator;
